/* eslint-disable no-underscore-dangle */
/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { gql } from '@apollo/client';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MaterialTable from 'material-table';
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Button,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import {
  faCaretDown,
  faCaretRight,
  faCheck,
  faLayerGroup,
  faSyncAlt,
  faTrash,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { Rating } from '@material-ui/lab';
import { StylesProvider } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';

import { mutate, query } from '../../../shared/apollo/ApolloClient';
import Metric from '../../../shared/components/Metric';
import { DateTimeRange } from '../../../shared/components/DateTime/DateTimeRangePicker';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import { generateClassName } from '../../../shared/styles/generateClassName';
import { clusteredColor, dangerColor, healthyColor } from '../../../shared/styles/colors';
import { createTableIcons } from '../../../shared/styles/createTableIcons';
import SnackConfig from '../../../shared/config/snackConfig';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { EnqueueJobRequest, Job, submitJob } from '../../../shared/services/job';
import TimeseriesExpression from './TimeseriesExpression';

const Accordion = withStyles({
  root: {
    boxShadow: 'none',
  },
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    padding: '0px',
    maxHeight: '24px',
    minHeight: '24px',
    '&.Mui-expanded': {
      minHeight: '24px',
    },
    justifyContent: 'flex-start',
  },
  content: {
    margin: '0px',
    flexGrow: 0,
  },
})(MuiAccordionSummary);

export interface MetricsTableRowData {
  primaryName: string;
  primaryMetricSelectors: string;
  secondaryName?: string;
  secondaryMetricSelectors?: string;
  value: string;
  hover?: number;
}

export type MetricsListData = Record<string, MetricsTableRowData[]>;

const useStylesJobItem = makeStyles(() => ({
  list: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

interface JobItemProps {
  job: Job;
  cluster: MetricsTableRowData[];
  columns: Record<string, unknown>[];
  actions?: MaterialTable<MetricsTableRowData>['props']['actions'];
  enableCloning: boolean;
  addJob?(job: Job): void;
  setActiveSpinner: (arg0: boolean) => void;
}

const JobItem: React.FC<JobItemProps> = ({
  job,
  cluster,
  columns,
  actions = [],
  enableCloning,
  addJob,
  setActiveSpinner,
}: JobItemProps) => {
  const classes = useStylesJobItem();

  const [clusterOpen, setClusterOpen] = useState<boolean>(job.type !== 'SIMILARITY_RANK_WITH_CLUSTERING');

  const [metricDeleterEnabled, setMetricDeleterEnabled] = useState<boolean>(false);
  const [metricsToBeRemoved, setMetricsToBeRemoved] = useState(new Set<string>());

  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));

  const removeMetricHandler = (rowData: MetricsTableRowData) => {
    const { primaryName, primaryMetricSelectors } = rowData;
    const selectorsToBeExcluded = JSON.parse(primaryMetricSelectors) as Record<string, string>;
    selectorsToBeExcluded.__name__ = primaryName;
    const selectorsToBeExcludedString = JSON.stringify(
      selectorsToBeExcluded,
      Object.keys(selectorsToBeExcluded).sort()
    );
    setMetricsToBeRemoved((previousMetricsToBeRemoved) => {
      const newMetricsToBeRemoved = new Set<string>();
      previousMetricsToBeRemoved.forEach((val: string) => {
        newMetricsToBeRemoved.add(val);
      });
      if (newMetricsToBeRemoved.has(selectorsToBeExcludedString)) {
        newMetricsToBeRemoved.delete(selectorsToBeExcludedString);
      } else {
        newMetricsToBeRemoved.add(selectorsToBeExcludedString);
      }
      return newMetricsToBeRemoved;
    });
  };

  const queueClonedJob = useCallback(() => {
    /**
     * Merge excludeSelectors from parent job with currently selected
     * metricsToBeRemoved Set.
     */
    const mergeExcludeSelectorsList = (parentExcludeSelectors: Record<string, string>[]): Record<string, string>[] => {
      const mergedSetOfExcludedSelectors = new Set<string>();
      parentExcludeSelectors.forEach((val: Record<string, string>) => {
        mergedSetOfExcludedSelectors.add(JSON.stringify(val, Object.keys(val).sort()));
      });
      metricsToBeRemoved.forEach((val: string) => {
        mergedSetOfExcludedSelectors.add(val);
      });
      return Array.from(mergedSetOfExcludedSelectors).map((metric) => JSON.parse(metric) as Record<string, string>);
    };

    const queueAJob = async () => {
      try {
        const { id: parentJobId, type, options } = job;
        setActiveSpinner(true);
        switch (type) {
          case 'RELEVANCE_RANKING': {
            const { startTime, endTime, selectors, excludeSelectors: parentExcludeSelectors } = options;
            const excludeSelectors =
              parentExcludeSelectors && parentExcludeSelectors.length > 0
                ? mergeExcludeSelectorsList(parentExcludeSelectors)
                : // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                  Array.from(metricsToBeRemoved).map((metric) => JSON.parse(metric));
            const jobRequest: EnqueueJobRequest = {
              type,
              options: {
                startTime,
                endTime,
                selectors,
                excludeSelectors,
              },
              parentJobId,
            };
            const data = await submitJob(jobRequest);
            addJob!(data!.enqueueJob);
            break;
          }
          default:
            console.warn(`queueClonedJob: Unknown job type ${type}`);
        }
      } catch (error) {
        setSnackConfig(
          new SnackConfig({
            open: true,
            message: JSON.stringify((error as Error).message),
          })
        );
      }

      setMetricsToBeRemoved(new Set<string>());
      setActiveSpinner(false);
    };

    void queueAJob();
  }, [metricsToBeRemoved, setActiveSpinner, job, addJob]);

  const checkIfCurrentRowIsExcluded = (rowData: MetricsTableRowData): boolean => {
    const currentRow = JSON.parse(rowData.primaryMetricSelectors) as Record<string, string>;
    currentRow.__name__ = rowData.primaryName;
    return metricsToBeRemoved.has(JSON.stringify(currentRow, Object.keys(currentRow).sort()));
  };

  const renderChart = useCallback(
    (rowData: MetricsTableRowData) => {
      const timeRange: DateTimeRange = {
        start: moment(job.options.startTime),
        end: moment(job.options.endTime),
      };
      switch (job.type) {
        case 'RELEVANCE_RANKING': {
          return (
            <Metric
              primaryName={rowData.primaryName}
              primaryMetricSelectors={rowData.primaryMetricSelectors}
              columnDefs={['time', 'value']}
              timeRange={timeRange}
              isRelative={false}
            />
          );
        }
        case 'SIMILARITY_RANK_WITH_CLUSTERING':
        case 'SIMILARITY_RANKING': {
          return (
            <Metric
              primaryName={rowData.primaryName}
              primaryMetricSelectors={rowData.primaryMetricSelectors}
              secondaryName={rowData.secondaryName}
              secondaryMetricSelectors={rowData.secondaryMetricSelectors}
              columnDefs={['time', 'value']}
              timeRange={timeRange}
              isRelative={false}
            />
          );
        }
        default:
          return null;
      }
    },
    [job]
  );

  return (
    <div>
      {job.type === 'SIMILARITY_RANK_WITH_CLUSTERING' && (
        <div
          className={classes.list}
          role="button"
          onClick={() => setClusterOpen((prevOpen) => !prevOpen)}
          aria-hidden="true"
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={clusterOpen ? faCaretDown : faCaretRight} />
          </ListItemIcon>
          <ListItemText primary={`Cluster: ${cluster.length} metric${cluster.length > 1 ? 's' : ''}`} />
        </div>
      )}
      <Collapse in={clusterOpen} timeout="auto" unmountOnExit>
        <StylesProvider generateClassName={generateClassName()}>
          <MaterialTable
            title=""
            // For some reason on Netlify, on opening the detail panel, the entire horizontalScroll
            // component gets an opacity of 0. A few clicks reverts back to normal. Enabling
            // doubleHorizontalScroll seems to be a workaround that works.
            options={{ emptyRowsWhenPaging: false, sorting: false, doubleHorizontalScroll: true }}
            columns={columns}
            localization={{
              header: {
                actions: metricDeleterEnabled ? 'Actions' : '',
              },
            }}
            actions={[
              ...actions,
              ...(enableCloning
                ? [
                    (rowData: MetricsTableRowData) => ({
                      icon: () => (
                        <FontAwesomeIcon
                          icon={faTrash}
                          color={checkIfCurrentRowIsExcluded(rowData) ? 'red' : 'default'}
                        />
                      ),
                      hidden: !metricDeleterEnabled,
                      tooltip: 'Remove metric',
                      onClick: () => removeMetricHandler(rowData),
                    }),
                    {
                      icon: () => <Button variant="outlined">Update Ranking</Button>,
                      hidden: metricDeleterEnabled,
                      isFreeAction: true,
                      onClick: () => setMetricDeleterEnabled((enabled) => !enabled),
                    },
                    {
                      icon: () => <Button variant="outlined">Run</Button>,
                      hidden: !metricDeleterEnabled,
                      isFreeAction: true,
                      onClick: () => {
                        queueClonedJob();
                        setMetricDeleterEnabled((enabled) => !enabled);
                      },
                    },
                    {
                      icon: () => <Button variant="outlined">Cancel</Button>,
                      isFreeAction: true,
                      hidden: !metricDeleterEnabled,
                      onClick: () => {
                        setMetricsToBeRemoved(new Set());
                        setMetricDeleterEnabled((enabled) => !enabled);
                      },
                    },
                  ]
                : []),
            ]}
            icons={createTableIcons()}
            data={cluster}
            detailPanel={renderChart}
            onRowClick={(event, rowData, togglePanel?) => {
              if (togglePanel) togglePanel();
            }}
          />
        </StylesProvider>
      </Collapse>
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getRenderDetails = (status: string) => {
  let icon;
  let color;

  if (status === 'SUCCEEDED') {
    icon = faCheck;
    color = healthyColor;
  } else if (status === 'QUEUED') {
    icon = faLayerGroup;
    color = 'black';
  } else if (status === 'IN_PROGRESS') {
    icon = faSyncAlt;
    color = 'black';
  } else {
    icon = faExclamationCircle;
    color = dangerColor;
  }

  return { icon, color };
};

const getCreatedOnText = (createdTime: string) => `Created on: ${createdTime}`;

const dateTimeFormat = 'ddd, MMM DD YYYY, hh:mm:ss a Z';

const getInfoText = (job: Job) => {
  let infoText;
  const { createdAt, completedAt, startedAt, status } = job;
  const createdTime = moment(createdAt).format(dateTimeFormat);
  const completedTime = moment(completedAt).format(dateTimeFormat);
  const startedTime = moment(startedAt).fromNow();

  if (status === 'SUCCEEDED') {
    infoText = `${getCreatedOnText(createdTime)}, Finished on: ${completedTime} - Succeeded`;
  } else if (status === 'QUEUED') {
    infoText = `${getCreatedOnText(createdTime)}, waiting in queue - Queued`;
  } else if (status === 'IN_PROGRESS') {
    infoText = `${getCreatedOnText(createdTime)}, started ${startedTime} - In progress`;
  } else {
    infoText = `${getCreatedOnText(createdTime)}, Finished on: ${completedTime} - Failed`;
  }
  return infoText;
};

const JOB_QUERY = gql`
  query job($id: Int32!) {
    job(id: $id) {
      createdAt
      startedAt
      completedAt
      options
      id
      status
      results
      type
      rating
      parentId
    }
  }
`;

interface JobVars {
  id: number;
  rating: number | null;
}

const UPDATE_JOB = gql`
  mutation updateJob($id: Int32!, $rating: Int) {
    updateJob(id: $id, rating: $rating)
  }
`;

const updateRating = async (jobId: number, rating: number | null) => {
  await mutate<unknown, JobVars>({
    mutation: UPDATE_JOB,
    variables: {
      id: jobId,
      rating,
    },
  });
};

interface SelectCallbacks {
  Add(id: number): void;
  Remove(id: number): void;
}

const useStylesJob = makeStyles(() => ({
  mtMaxBorder: {
    display: 'block !important',
  },
  item: {
    wordBreak: 'break-all',
  },
  permalink: {
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  clustered: {
    color: clusteredColor,
  },
  ranking: {
    marginRight: '10px',
  },
  topBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

interface OnlineMetricsJobProps {
  job: Job;
  transformResults(job: Job): MetricsListData;
  columns: Record<string, unknown>[];
  actions?: MaterialTable<MetricsTableRowData>['props']['actions'];
  updateJob?: (id: number, job: Job) => void;
  enableCloning: boolean;
  addJob?(job: Job): void;
  selectCallbacks?: SelectCallbacks;
}

const OnlineMetricsJob: React.FC<OnlineMetricsJobProps> = ({
  job,
  transformResults,
  columns,
  actions = [],
  updateJob,
  enableCloning,
  addJob,
  selectCallbacks,
}: OnlineMetricsJobProps) => {
  const classes = useStylesJob();
  const [open, setOpen] = useState<boolean>(false);
  const [resultData, setResultData] = useState<MetricsListData>({});
  const [shouldGetResultData, setShouldGetResultData] = useState<boolean>(false);
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);
  const [itemComponent, setItemComponent] = useState<React.ReactElement | null>();

  const [currentJob, setCurrentJob] = useState(job);
  const [currentJobTime, setCurrentJobTime] = useState(moment());
  const [refreshErrorCount, setRefreshErrorCount] = useState(0);
  const [rating, setRating] = useState<number | null>(null);

  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));

  useEffect(() => {
    setCurrentJobTime(moment());
  }, [job]);

  useEffect(() => {
    setRating(currentJob.rating);
  }, [currentJob.rating]);

  useEffect(() => {
    const { status } = currentJob;
    if (status === 'SUCCEEDED' || status === 'FAILED') {
      return;
    }
    if (refreshErrorCount >= 3) {
      setSnackConfig(new SnackConfig({ open: true, message: 'Failed to refresh the status of the job' }));
      return;
    }
    let refreshInterval = 10000;
    if (moment().diff(currentJobTime, 's') > 30) {
      refreshInterval = 10000;
    } else if (moment().diff(currentJobTime, 's') > 10) {
      refreshInterval = 5000;
    } else {
      refreshInterval = 2000;
    }

    void (async () => {
      await delay(refreshInterval);
      const { id } = currentJob;
      try {
        const data = await query<Record<string, Job>>({
          query: JOB_QUERY,
          variables: {
            id,
          },
          fetchPolicy: 'network-only',
        });
        setCurrentJob({ ...data!.job });
      } catch (error) {
        setRefreshErrorCount((prevCount) => prevCount + 1);
        console.error(
          `Error in refreshing the job id - ${id}, refreshCount - ${refreshErrorCount}: ${JSON.stringify(error)}`
        );
      }
    })();
  }, [currentJob, currentJobTime, refreshErrorCount]);

  useEffect(() => {
    void (async (): Promise<void> => {
      const { id: jobId, type, options, status, parentId } = currentJob;
      const start = moment(options.startTime).format('MMM DD, hh:mm a');
      const end = moment(options.endTime).format('MMM DD, hh:mm a');

      let parentCreatedAtText;
      if (parentId) {
        const data = await query<Record<string, Job>>({
          query: JOB_QUERY,
          variables: {
            id: parentId,
          },
        });
        const { createdAt } = data!.job;
        parentCreatedAtText = getCreatedOnText(moment(createdAt).format(dateTimeFormat));
      }
      const onChangeRating = async (newRating: number | null) => {
        const oldRating = rating;
        setRating(newRating);
        try {
          await updateRating(jobId, newRating);
          updateJob!(jobId, { ...currentJob, rating: newRating });
        } catch (error) {
          // Reset back to old rating
          setRating(oldRating);
          setSnackConfig(new SnackConfig({ open: true, message: JSON.stringify(error) }));
        }
      };

      const ratingComp = status === 'SUCCEEDED' && (
        <Rating
          className={classes.ranking}
          // name must be unique amongst parent list's items
          name={jobId.toString()}
          value={rating}
          onChange={(event, newRating) => {
            void onChangeRating(newRating);
          }}
        />
      );

      const clusteredComp = type === 'SIMILARITY_RANK_WITH_CLUSTERING' && (
        <span className={classes.clustered}>Clustered</span>
      );

      const infoText = getInfoText(currentJob);

      const timeRangeComp = (
        <ListItemText
          primary={
            <span>
              <b>Time range: </b> {`${start} to ${end}`}
            </span>
          }
        />
      );

      const permalinkComp = (
        <ListItemText
          secondary={
            <Link to={`/job/${jobId}`} className={classes.permalink}>
              <Typography color="textSecondary" variant="body2">
                {infoText}
              </Typography>
            </Link>
          }
        />
      );

      switch (type) {
        case 'SIMILARITY_RANK_WITH_CLUSTERING':
        case 'SIMILARITY_RANKING': {
          setItemComponent(
            <div>
              <div className={classes.topBar}>
                {ratingComp}
                {clusteredComp}
              </div>
              <ListItemText
                primary={
                  <span className={classes.item}>
                    <b>Issue timeseries: </b> {options.primarySelectors}
                  </span>
                }
              />
              <ListItemText
                primary={
                  <span className={classes.item}>
                    <b>Candidate space: </b> {options.secondarySelectors!.join('|')}
                  </span>
                }
              />
              {timeRangeComp}
              {permalinkComp}
            </div>
          );
          break;
        }
        case 'RELEVANCE_RANKING':
          setItemComponent(
            <div>
              {ratingComp}
              <ListItemText
                primary={
                  <span className={classes.item}>
                    <b>Search space: </b> {options.selectors!.join('|')}
                  </span>
                }
              />
              {options.excludeSelectors && options.excludeSelectors.length > 0 && (
                <ListItemText
                  primary={
                    <Accordion TransitionProps={{ unmountOnExit: true }}>
                      <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
                        <Typography>
                          <b>Excluded timeseries: </b> {options.excludeSelectors.length}{' '}
                          {parentId && (
                            <Link to={`/job/${parentId}`} className={classes.permalink}>
                              <Typography component="span" color="textSecondary" variant="body2">
                                (parent - {parentCreatedAtText})
                              </Typography>
                            </Link>
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <ul>
                          {options.excludeSelectors.map((selector) => {
                            const selectorClone = { ...selector };
                            delete selectorClone.__name__;
                            return (
                              <li key={JSON.stringify(selector)}>
                                <TimeseriesExpression name={selector.__name__} labels={JSON.stringify(selectorClone)} />
                              </li>
                            );
                          })}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  }
                />
              )}
              {timeRangeComp}
              {permalinkComp}
            </div>
          );
          break;
        default:
          setItemComponent(<ListItemText primary="Unknown job type" />);
          break;
      }
    })();
    // classes is never changed. So we don't need a dependency on it. However if we do take a dependency, we will enter
    // an infinite recursion as setItemComponent triggeres re-render which re-initiates classes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating, currentJob, updateJob]);

  const renderData = useCallback(() => {
    return (
      <List component="div" disablePadding>
        {Object.entries(resultData).map(([clusterId, cluster]) => (
          <ListItem key={clusterId} className={classes.mtMaxBorder}>
            <JobItem
              job={currentJob}
              cluster={cluster}
              columns={columns}
              actions={actions}
              enableCloning={enableCloning}
              addJob={addJob}
              setActiveSpinner={setActiveSpinner}
            />
          </ListItem>
        ))}
      </List>
    );
  }, [actions, addJob, classes.mtMaxBorder, columns, currentJob, enableCloning, resultData]);

  useEffect(() => {
    // If a new job comes in, we need to fetch the results again
    setShouldGetResultData(true);
  }, [currentJob, transformResults]);

  useEffect(() => {
    const updateData = () => {
      try {
        // Only attempt fetching if the row is expanded and it's the first time
        if (shouldGetResultData && open) {
          if (currentJob.status === 'SUCCEEDED') {
            setActiveSpinner(true);
            setResultData({});
            const data = transformResults(currentJob);
            setResultData(data);
          }
          // Once we have got the data successfully, we need not re-fetch it
          setShouldGetResultData(false);
        }
      } catch (error) {
        setResultData({});
        setSnackConfig(
          new SnackConfig({
            open: true,
            message: JSON.stringify(error),
          })
        );
      }
      setActiveSpinner(false);
    };
    void updateData();
    // If any changes needed to be done due to job or transformResults, that's handled in the above useEffect block
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldGetResultData, open]);

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      selectCallbacks!.Add(currentJob.id);
    } else {
      selectCallbacks!.Remove(currentJob.id);
    }
  };

  const { status } = currentJob;
  const { icon, color } = getRenderDetails(status);
  return (
    <div>
      {itemComponent && (
        <div>
          <ListItem>
            {selectCallbacks && (
              <Checkbox
                color="default"
                inputProps={{ 'aria-label': 'checkbox with default color' }}
                onChange={handleSelect}
              />
            )}
            <ListItemIcon>
              <FontAwesomeIcon icon={icon} spin={status === 'IN_PROGRESS'} color={color} />
            </ListItemIcon>
            {itemComponent}
            {status === 'SUCCEEDED' && (
              <ListItemSecondaryAction onClick={() => setOpen((prevOpen) => !prevOpen)}>
                {open ? <ExpandLess /> : <ExpandMore />}
              </ListItemSecondaryAction>
            )}
          </ListItem>
          {status === 'SUCCEEDED' && (
            <Collapse in={open} timeout="auto" unmountOnExit>
              <SpinnerOverlay active={activeSpinner}>{renderData()}</SpinnerOverlay>
            </Collapse>
          )}
          <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
        </div>
      )}
    </div>
  );
};

OnlineMetricsJob.defaultProps = {
  updateJob: () => {},
  addJob: () => {},
  selectCallbacks: undefined,
};

export default OnlineMetricsJob;
